// 📄 components/StepInvoicePosting.tsx
import React from 'react';
import { AlertCircle } from 'lucide-react';
import { InvoiceFormData } from '../../../../../services/commerce/integrationService';

interface StepInvoicePostingProps {
  glDrPostingKey: string;
  glCrPostingKey: string;
  vendDrPostingKey: string;
  vendCrPostingKey: string;
  invoiceDocType: string;
  onChange: (field: keyof InvoiceFormData, value: any) => void;
  isInvalid: boolean;
}

export function StepInvoicePosting({ glDrPostingKey, glCrPostingKey, vendDrPostingKey, vendCrPostingKey, invoiceDocType, onChange, isInvalid }: StepInvoicePostingProps) {
  return (
    <div className="space-y-4 animate-fadeIn">
      <div className="bg-blue-500/5 border border-blue-500/10 rounded-xl p-3 mb-2 space-y-1">
        <span className="text-[10px] font-bold text-blue-400 uppercase tracking-widest block">Ruta SPRO / Transacción SAP</span>
        <p className="font-mono text-xs text-gray-300">
          <span className="bg-zinc-800 px-1.5 py-0.5 rounded text-gray-400 font-bold mr-1.5">OBXZ / OB40</span>
          <span>Financial Accounting → Accounts Receivable and Payable → Business Transactions → Incoming Invoices/Credit Memos → Define Posting Keys</span>
        </p>
      </div>

      <div>
        <h3 className="text-lg font-medium text-blue-400">3. Program Parameters & Posting Keys</h3>
        <p className="text-sm text-gray-400">
          Define las claves de contabilización (Posting Keys) usadas al generar el documento de factura y el tipo de documento asociado.
        </p>
      </div>

      <div className="bg-white/5 p-4 rounded-xl border border-white/5 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">G/L Dr (Debe)</label>
          <input
            type="text"
            value={glDrPostingKey}
            onChange={(e) => onChange('glDrPostingKey', e.target.value.replace(/[^0-9]/g, ''))}
            placeholder="40"
            maxLength={2}
            className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm font-mono text-white focus:outline-none focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">G/L Cr (Haber)</label>
          <input
            type="text"
            value={glCrPostingKey}
            onChange={(e) => onChange('glCrPostingKey', e.target.value.replace(/[^0-9]/g, ''))}
            placeholder="50"
            maxLength={2}
            className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm font-mono text-white focus:outline-none focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">
            Document Type <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={invoiceDocType}
            onChange={(e) => onChange('invoiceDocType', e.target.value.toUpperCase())}
            placeholder="RE"
            maxLength={2}
            className={`w-full bg-zinc-800 border rounded-lg p-2.5 text-sm font-mono text-white focus:outline-none focus:border-blue-500 ${
              isInvalid && !invoiceDocType ? 'border-red-500' : 'border-white/10'
            }`}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">Vend Dr (Debe Proveedor)</label>
          <input
            type="text"
            value={vendDrPostingKey}
            onChange={(e) => onChange('vendDrPostingKey', e.target.value.replace(/[^0-9]/g, ''))}
            placeholder="21"
            maxLength={2}
            className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm font-mono text-white focus:outline-none focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">Vend Cr (Haber Proveedor)</label>
          <input
            type="text"
            value={vendCrPostingKey}
            onChange={(e) => onChange('vendCrPostingKey', e.target.value.replace(/[^0-9]/g, ''))}
            placeholder="31"
            maxLength={2}
            className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm font-mono text-white focus:outline-none focus:border-blue-500"
          />
        </div>
      </div>

      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 text-blue-400 flex gap-3 text-sm">
        <AlertCircle className="w-5 h-5 flex-shrink-0" />
        <div>
          <p className="font-semibold">Consistencia con el esquema de contabilización</p>
          <p className="text-xs text-blue-400/80 mt-0.5">
            Verifica que estas claves coincidan con las usadas por MIRO/MIR7 en este mandante; un desajuste generará documentos contables desbalanceados.
          </p>
        </div>
      </div>
    </div>
  );
}
